import React from 'react'

import { Alert, Typography } from '@barracuda-internal/bds-core'
import styles from 'components/pages/support/help/helpStyles'
import Preview from 'assets/images/barracuda/email_banner_preview.png'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const BASE_I18N_KEY = 'ess.help_inbound_email_banners'
const CAMPUS_URL = 'https://documentation.campus.barracuda.com/wiki/x/SoAr'

function InboundEmailBannersI18n({
  classes,
  productName
}: {
  classes: ReturnType<typeof styles>
  productName?: string
}) {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  return (
    <div className={classes.content} data-testid="content">
      <p>
        {formatMessage('intro_prefix')}
        <a href={CAMPUS_URL} target="_blank" rel="noreferrer">
          &nbsp;{formatMessage('intro_link')}
        </a>
      </p>
      {formatMessage('note', { productName })}
      <br />
      <br />
      <img src={Preview} alt="Preview email banners" />
      <br />
      <br />
      <b>{formatMessage('banner_types_title')}</b>
      <br />
      <br />
      <div className={`${classes.bannerRectangle} ${classes.infoBanner}`}>
        <Typography variant="h5">{formatMessage('banners.external_sender.title')}</Typography>
        <span>{formatMessage('banners.external_sender.message')}</span>
      </div>
      <br />
      <div className={`${classes.bannerRectangle} ${classes.warningBanner}`}>
        <Typography variant="h5">{formatMessage('banners.unusual_sender.title')}</Typography>
        <span>{formatMessage('banners.unusual_sender.message')}</span>
      </div>
      <br />
      <div className={`${classes.bannerRectangle} ${classes.warningBanner}`}>
        <Typography variant="h5">{formatMessage('banners.unusual_sender_ip.title')}</Typography>
        <span>{formatMessage('banners.unusual_sender_ip.message')}</span>
      </div>
      <br />
      <div className={`${classes.bannerRectangle} ${classes.warningBanner}`}>
        <Typography variant="h5">{formatMessage('banners.unusual_link.title')}</Typography>
        <span>{formatMessage('banners.unusual_link.message')}</span>
      </div>
    </div>
  )
}

function InboundEmailBanners(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  // Flag ON: Use translated content
  if (isSmartlingEnabled) {
    return <InboundEmailBannersI18n classes={classes} productName={props.productName} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      <p>
        Email warning banners are designed to alert end users about the types of potential threats that may exist within
        a given email. If enabled, a banner will display at the top of the suspicious emails.
        <a href="https://documentation.campus.barracuda.com/wiki/x/SoAr" target="_blank" rel="noreferrer">
          &nbsp;Learn more
        </a>
      </p>
      <b>Note</b>: These settings apply to all domains you have verified in {props.productName} for processing email.
      You are unable to change these settings for a specific domain.
      <br />
      <br />
      <img src={Preview} alt="Preview email banners" />
      <br />
      <br />
      <b>Email warning banner types:</b>
      <br />
      <br />
      <div className={`${classes.bannerRectangle} ${classes.infoBanner}`}>
        <Typography variant="h5">External sender</Typography>
        <span>Confirm you trust this sender before taking any actions.</span>
      </div>
      <br />
      <div className={`${classes.bannerRectangle} ${classes.warningBanner}`}>
        <Typography variant="h5">Warning: Unusual sender &lt;sender@email.com&gt;</Typography>
        <span>
          You don&apos;t usually receive emails from this address. Confirm you trust this sender before taking any
          actions.
        </span>
      </div>
      <br />
      <div className={`${classes.bannerRectangle} ${classes.warningBanner}`}>
        <Typography variant="h5">Warning: Unusual sender IP</Typography>
        <span>
          This message originated from a source not commonly seen for this domain, which could be an indication of a
          scam.
        </span>
      </div>
      <br />
      <div className={`${classes.bannerRectangle} ${classes.warningBanner}`}>
        <Typography variant="h5">Warning: Unusual link</Typography>
        <span>
          This message contains an unusual link, which may lead to a malicious site. Confirm the message is safe before
          clicking any links.
        </span>
      </div>
    </div>
  )
}

export default InboundEmailBanners
